import { useMemo } from 'react';

import { BookingModeDto, DictionaryTypeDto } from '@/api/generated';
import { useGetDictionaryTypesCalendarConfigurations } from '@/api/requests/useGetDictionaryTypesCalendarConfigurations.util.ts';
import { SelectSearchOptionModel } from '@/components/form/model/SelectSearchOption.model.ts';
import { useDictionaryTypeLabel } from '@/views/processes/calendarEntryQuery/util/useDictionaryTypeLabel.util.ts';
import { processCalendarEntryQueryUtil } from '@/views/processes/calendarEntryQuery/utils/processCalendarEntryQuery.util.ts';
import { useGetDictionaryOptionsByTypes } from '@/views/processes/offerInstance/util/useGetDictionaryOptionsByTypes.util.ts';

type Return = {
  bookingMode: BookingModeDto;
  /** Whether the type has calendars; undefined while its configuration is unknown. */
  calendarsEnabled?: boolean;
  entryOptions: SelectSearchOptionModel<number>[];
  /** The selected entry is inactive, so it cannot get a rezerwacja wstępna. */
  isEntryInactive: boolean;
  /** The booking mode failed to load: bookingMode is unknown, block submitting like while loading. */
  isError: boolean;
  /** While true, bookingMode is not known yet: block submitting so exact times are not truncated. */
  isLoading: boolean;
  typeLabel: (type: DictionaryTypeDto) => string;
  typeOptions: SelectSearchOptionModel<DictionaryTypeDto>[];
};

// Every type but a shared one is allowed: the query is metadata, calendars do not have to be enabled.
export const useProcessCalendarEntryQueryOptions = (
  type?: DictionaryTypeDto | null,
  entryId?: number | null,
): Return => {
  const typeLabel = useDictionaryTypeLabel();
  const types = useMemo(() => (type ? [type] : []), [type]);
  const entryIds = useMemo(() => (entryId ? [entryId] : []), [entryId]);
  const {
    configurationByType,
    inactiveDictionaryNamesById,
    isConfigurationError,
    isLoading,
    optionsByType,
  } = useGetDictionaryOptionsByTypes(types, entryIds);
  const { configurationByType: configurationOfAllTypes } =
    useGetDictionaryTypesCalendarConfigurations();
  const typeOptions = useMemo(
    () =>
      processCalendarEntryQueryUtil
        .queryableTypes(Object.values(DictionaryTypeDto), configurationOfAllTypes, type)
        .map(value => ({ label: typeLabel(value), value })),
    [configurationOfAllTypes, type, typeLabel],
  );

  return {
    bookingMode: processCalendarEntryQueryUtil.bookingMode(
      type ? configurationByType[type] : undefined,
    ),
    calendarsEnabled: type ? configurationByType[type]?.calendarsEnabled : undefined,
    entryOptions: (type ? optionsByType[type] : undefined) ?? [],
    isEntryInactive: Boolean(entryId && inactiveDictionaryNamesById.has(entryId)),
    isError: isConfigurationError,
    isLoading,
    typeLabel,
    typeOptions,
  };
};
